<template>
	<v-dialog scrollable persistent v-model="showDialog" max-width="1000">
		<v-card>
			<v-card-title>Deleted Entries</v-card-title>
			<v-container
				fluid
				class="flexContainerItem pb-0"
				style="overflow: hidden"
			>
				<div class="grid-flex-container">
					<div class="grid-toolbar">
						<quick-filter
							:Columns="visibleColumns"
							:filter="quickFilter"
							@filterChanged="onQuickFilterChanged"
						/>
						<v-badge
							:value="advancedFilter.FiltersCount > 0"
							left
							class="d-none d-lg-flex"
							bordered
							overlap
						>
							<template v-slot:badge>
								<span style="color: white">{{
									advancedFilter.FiltersCount
								}}</span>
							</template>
							<v-btn
								color="primary"
								@click="onShowAdvancedFilter"
							>
								<v-icon class="mr-1">mdi-magnify</v-icon>
								Advanced search
							</v-btn>
						</v-badge>
						<v-menu offset-y>
							<template v-slot:activator="{ on, attrs, value }">
								<v-btn
									v-bind="attrs"
									v-on="on"
									class="export d-none d-lg-flex"
									color="primary"
								>
									<v-icon class="mr-1"
										>mdi-cloud-download</v-icon
									>
									Export
									<v-icon v-if="value">mdi-menu-up</v-icon>
									<v-icon v-else>mdi-menu-down</v-icon>
								</v-btn>
							</template>
							<v-list>
								<template v-for="(item, i) in exportOptions">
									<item-group
										class="d-none d-lg-flex"
										v-if="item.children"
										:key="`grid-group-${i}`"
										:item="item"
									/>
									<list-item
										class="d-none d-lg-flex"
										v-else
										:key="`grid-item-${i}`"
										:item="item"
									/>
								</template>
							</v-list>
						</v-menu>

						<v-menu offset-y :close-on-content-click="false">
							<template v-slot:activator="{ on, attrs }">
								<v-btn
									v-bind="attrs"
									v-on="on"
									class="d-lg-none"
									color="primary"
								>
									<v-icon>mdi-dots-vertical</v-icon>
								</v-btn>
							</template>
							<v-list class="d-lg-none">
								<template
									v-for="(item, i) in responsiveMenuItems"
								>
									<item-group
										v-if="item.children"
										:key="`grid-group-${i}`"
										:item="item"
									/>
									<list-item
										v-else
										:key="`grid-item-${i}`"
										:item="item"
									/>
								</template>
							</v-list>
						</v-menu>
					</div>
					<div class="generic-grid-container">
						<grid-view
							v-if="visibleColumns"
							:gridData="gridData"
							:irisUserMetadata="irisUserMetadata"
							:loadingData="gridLoading"
							:visibleColumns="visibleColumns"
							:sorting="sorting"
							:page="page"
							:rowsPerPage="rowsPerPage"
							:totalCount="totalCount"
							:deletedGrid="true"
							:hasActions="true"
							:selectedRows="selectedRows"
							:multipleSelection="canBulkRestore"
							@rowsPerPageChanged="onRowsPerPageChanged"
							@pageChanged="onPageChanged"
							@sortChanged="onSortChanged"
							@rowSelectionChanged="onRowSelectionChanged"
							@bulkRestore="bulkRestore"
						>
							<template v-slot:[`actionsColumns`]="{ item }">
								<td class="fixed-col">
									<v-btn
										fab
										small
										icon
										elevation="1"
										class="action-btn"
										@click="openItemHistory(item.Id)"
									>
										<v-icon color="info" small
											>mdi-history</v-icon
										>
									</v-btn>
									<v-btn
										fab
										small
										icon
										elevation="1"
										class="action-btn"
										@click="restoreItem(item.Id)"
									>
										<v-icon color="primary" small
											>mdi-delete-restore</v-icon
										>
									</v-btn>
								</td>
							</template>
						</grid-view>
					</div>
				</div>
			</v-container>
			<v-card-actions>
				<v-spacer />
				<v-btn @click.stop="onClose">Close</v-btn>
			</v-card-actions>
		</v-card>
		<filters-dialog
			v-if="showAdvancedFilter"
			:dialog="showAdvancedFilter"
			:filter="advancedFilter"
			:columns="visibleColumns"
			@filterChanged="onAdvancedFilterChanged"
		/>
	</v-dialog>
</template>

<script>
import { mapGetters } from 'vuex';
import FiltersDialog from '../../shared/gridViews/FiltersDialog.vue';
import QuickFilter from '../../shared/gridViews/QuickFilter.vue';
import GridView from '../../shared/gridViews/GridView.vue';
import ItemGroup from '@/components/base/ItemGroup';
import ListItem from '@/components/base/ListItem';
import { GridsPageDeletedGetters } from '../../../store/getters';
import { GridsPageDeletedActions } from '../../../store/actions';
import { Permissions } from '../../../enums/permissionsEnum';
import ConfirmDialog from '@/components/core/dialogs/confirmDialog';
import permissionsMixin from '../../../mixins/permissionsMixin';
export default {
	name: 'GridTableDeleted',
	props: {
		showDialog: Boolean,
		defaultGridView: Object,
	},
	components: { GridView, FiltersDialog, QuickFilter, ListItem, ItemGroup },
	mixins: [permissionsMixin],
	data() {
		return {
			showAdvancedFilter: false,
			multipleSelection: false,
			selectedRows: [],
		};
	},
	computed: {
		...mapGetters({
			//for toolbar only
			quickFilter: [GridsPageDeletedGetters.QUICK_FILTER],
			gridView: [GridsPageDeletedGetters.GRID_VIEW],
			advancedFilter: [GridsPageDeletedGetters.ADVANCED_FILTER],
			hiddenColumns: [GridsPageDeletedGetters.HIDDEN_COLUMNS],
			//for toolbar & grid
			visibleColumns: [GridsPageDeletedGetters.VISIBLE_COLUMNS],
			//for grid only
			gridData: [GridsPageDeletedGetters.GRID_DATA],
			irisUserMetadata: [GridsPageDeletedGetters.IRIS_USER_METADATA],
			gridLoading: [GridsPageDeletedGetters.GRID_LOADING],
			sorting: [GridsPageDeletedGetters.SORTING],
			page: [GridsPageDeletedGetters.PAGE],
			totalCount: [GridsPageDeletedGetters.TOTAL_COUNT],
			rowsPerPage: [GridsPageDeletedGetters.ROWS_PER_PAGE],
		}),
		canBulkRestore() {
			return this.hasPermissions([Permissions.Grids.BulkRestore]);
		},
		entityType() {
			return this.$route.params.pageName;
		},
		responsiveMenuItems() {
			return [
				{
					title: 'Export',
					children: this.exportOptions,
				},
				{
					title: 'Advanced search',
					clickHandler: () => this.openAdvancedSearch(),
				},
			];
		},
		exportOptions() {
			return [
				{
					title: 'Visible data',
					clickHandler: () =>
						this.$store.dispatch(
							GridsPageDeletedActions.EXPORT_CURRENT_GW_PAGE
						),
				},
				{
					title: 'All data',
					clickHandler: () =>
						this.$store.dispatch(
							GridsPageDeletedActions.EXPORT_ALL_DATA
						),
				},
			];
		},
	},
	mounted() {
		this.$store.dispatch(
			GridsPageDeletedActions.LOAD_GRID_VIEW,
			this.defaultGridView
		);
	},
	destroyed() {
		this.$store.dispatch(GridsPageDeletedActions.CLEAR);
	},
	methods: {
		multipleSelectionClick() {
			this.multipleSelection = true;
		},
		onRowSelectionChanged(rows, selectAll) {
			if (!rows) return;
			rows.forEach((row) => {
				let index = this.selectedRows.indexOf(row);
				if (selectAll === null) {
					//single row click
					if (index >= 0) {
						this.selectedRows.splice(index, 1);
					} else {
						this.selectedRows.push(row);
					}
				} else {
					//select all
					if (selectAll) {
						if (index < 0) this.selectedRows.push(row);
					} else {
						if (index >= 0) this.selectedRows.splice(index, 1);
					}
				}
			});
		},
		onClose() {
			this.$emit('close');
		},
		onPageChanged(value) {
			this.$store.dispatch(GridsPageDeletedActions.UPDATE_PAGE, value);
		},
		onRowsPerPageChanged(value) {
			this.$store.dispatch(
				GridsPageDeletedActions.UPDATE_ROWS_PER_PAGE,
				value
			);
		},
		onSortChanged(value) {
			this.$store.dispatch(GridsPageDeletedActions.UPDATE_SORTING, value);
		},
		onQuickFilterChanged(value) {
			this.$store.dispatch(
				GridsPageDeletedActions.UPDATE_QUICK_FILTER,
				value
			);
		},
		onShowAdvancedFilter() {
			this.showAdvancedFilter = true;
		},
		onAdvancedFilterChanged(value) {
			this.$store.dispatch(
				GridsPageDeletedActions.UPDATE_ADVANCED_FILTER,
				value
			);
			this.showAdvancedFilter = false;
		},
		restoreItem(id) {
			ConfirmDialog({
				message: 'Are you sure you want to restore this asset?',
			}).then((isRestore) => {
				if (isRestore) {
					this.$store
						.dispatch(GridsPageDeletedActions.LOAD_RECORD, id)
						.then(() => {
							this.$store.dispatch(
								GridsPageDeletedActions.RESTORE_DATA
							);
						});
				}
			});
		},
		bulkRestore() {
			ConfirmDialog({
				message: `WARNING! You are about to restore ${this.selectedRows.length} records. Are you sure you want to restore those items?`,
				modalWidth: '600',
			}).then((isRestore) => {
				if (isRestore) {
					this.$store
						.dispatch(GridsPageDeletedActions.BULK_RESTORE_DATA, {
							SelectedEntityName: this.entityType,
							SelectedRows: this.selectedRows,
						})
						.then((succes) => {
							if (succes) this.selectedRows = [];
						});
				}
			});
		},
		openItemHistory(Id) {
			this.$router.push(`${this.gridView.Entity.Name}/history/${Id}`);
		},
	},
};
</script>
